import { isHandoverFeeCode } from './feeCategoryCodeGrouping';

export type ServiceFeeDebtInvoice = {
  id?: string;
  apartmentId?: string;
  invoiceType?: string | null;
  openingDebt: number;
  amount: number;
  baseAmount?: number | null;
  paidAmount: number;
  surchargeAmount?: number;
  vatAmount?: number | null;
  exemptionAmount?: number;
  status?: string;
  billingPeriod?: string;
  feeCategoryId?: string | null;
  monthlyVehicleId?: string | null;
  utilityMeterId?: string | null;
};

/** Match backend PAYABLE_SERVICE_FEE_INVOICE_STATUSES — ADJUSTED is still owed until paid. */
const UNPAID_STATUSES = new Set(['PENDING', 'OVERDUE', 'PARTIAL', 'ADJUSTED']);

export function parseBillingPeriodValue(period: string): { month: number; year: number } {
  const normalized = period.replace(/^Tháng\s+/i, '').trim();
  const [monthStr, yearStr] = normalized.split('/');
  return {
    month: parseInt(monthStr, 10),
    year: parseInt(yearStr, 10),
  };
}

export function compareBillingPeriods(a: string, b: string): number {
  const pa = parseBillingPeriodValue(a);
  const pb = parseBillingPeriodValue(b);
  if (pa.year !== pb.year) return pa.year - pb.year;
  return pa.month - pb.month;
}

const CASUAL_PARKING_FEE_CODE = 'T-VL001';

function isStandaloneActivityInvoice(invoice: {
  invoiceType?: string | null;
  feeCode?: string | null;
}): boolean {
  if (invoice.invoiceType === 'CASUAL_PARKING' || invoice.invoiceType === 'CONTRACT') {
    return true;
  }
  const code = (invoice.feeCode ?? '').trim().toUpperCase();
  return code === CASUAL_PARKING_FEE_CODE || code.startsWith('T-KTDV');
}

export function getInvoiceChainKey(invoice: {
  id?: string;
  apartmentId?: string;
  feeCategoryId?: string | null;
  feeCode?: string | null;
  monthlyVehicleId?: string | null;
  utilityMeterId?: string | null;
  invoiceType?: string | null;
}): string {
  if (invoice.monthlyVehicleId) return `mv:${invoice.monthlyVehicleId}`;
  if (invoice.utilityMeterId) return `um:${invoice.utilityMeterId}`;
  if (isStandaloneActivityInvoice(invoice)) {
    return `id:${invoice.id ?? 'unknown'}`;
  }
  const aptPrefix = invoice.apartmentId ? `${invoice.apartmentId}:` : '';
  if (invoice.feeCategoryId) return `${aptPrefix}fc:${invoice.feeCategoryId}`;
  return `id:${invoice.id ?? 'unknown'}`;
}

export function getIncurredBeforeTax(invoice: {
  amount: number;
  baseAmount?: number | null;
  vatAmount?: number | null;
}): number {
  if (invoice.baseAmount != null) {
    return invoice.baseAmount;
  }

  const vat = invoice.vatAmount ?? 0;
  if (vat > 0 && invoice.amount > vat) {
    return invoice.amount - vat;
  }
  return invoice.amount;
}

export function getInvoiceAmountAfterTax(invoice: {
  amount: number;
  baseAmount?: number | null;
  vatAmount?: number | null;
}): number {
  if (invoice.baseAmount != null && invoice.vatAmount != null) {
    const fromParts = invoice.baseAmount + invoice.vatAmount;
    if (fromParts > 0 || Math.round(invoice.amount) === 0) {
      return fromParts;
    }
  }
  if (invoice.baseAmount != null) {
    const fromBase = invoice.baseAmount + (invoice.vatAmount ?? 0);
    if (fromBase > 0 || Math.round(invoice.amount) === 0) {
      return fromBase;
    }
  }
  return invoice.amount;
}

/** Apartment Phí dịch vụ list — CNNBG debt is in openingDebt, not amount. */
export function getInvoiceListDisplayAmount(
  invoice: ServiceFeeDebtInvoice & {
    feeCategory?: { feeCode?: string | null } | null;
    fee?: { feeCategory?: { feeCode?: string | null } } | null;
  },
): number {
  const feeCode = invoice.feeCategory?.feeCode ?? invoice.fee?.feeCategory?.feeCode;
  if (isHandoverFeeCode(feeCode)) {
    return computeInvoiceClosingDebt(invoice);
  }
  return getInvoiceAmountAfterTax(invoice);
}

/** Shown under Số tiền on căn hộ Phí dịch vụ when a payment was posted. */
export function getInvoicePaymentProgress(
  invoice: ServiceFeeDebtInvoice & { closingDebt?: number | null },
): { paidAmount: number; remainingDebt: number } | null {
  const paidAmount = invoice.paidAmount ?? 0;
  if (paidAmount <= 0) {
    return null;
  }
  const remainingDebt =
    invoice.closingDebt != null && !Number.isNaN(invoice.closingDebt)
      ? Math.max(0, invoice.closingDebt)
      : computeInvoiceClosingDebt(invoice);
  return { paidAmount, remainingDebt };
}

export function computeTotalReceivable(
  openingDebt: number,
  amount: number,
  surchargeAmount = 0,
  vatAmount = 0,
  exemptionAmount = 0,
): number {
  return openingDebt + amount + surchargeAmount + vatAmount - exemptionAmount;
}

export function computeClosingDebt(
  openingDebt: number,
  amount: number,
  paidAmount: number,
  surchargeAmount = 0,
  vatAmount = 0,
  exemptionAmount = 0,
): number {
  const totalReceivable = computeTotalReceivable(
    openingDebt,
    amount,
    surchargeAmount,
    vatAmount,
    exemptionAmount,
  );
  return Math.max(0, totalReceivable - paidAmount);
}

export function computeInvoiceClosingDebt(invoice: ServiceFeeDebtInvoice): number {
  const incurred = getIncurredBeforeTax(invoice);
  return computeClosingDebt(
    invoice.openingDebt,
    incurred,
    invoice.paidAmount,
    invoice.surchargeAmount ?? 0,
    invoice.vatAmount ?? 0,
    invoice.exemptionAmount ?? 0,
  );
}

export function getInvoiceTotalReceivable(invoice: ServiceFeeDebtInvoice): number {
  const incurred = getIncurredBeforeTax(invoice);
  return computeTotalReceivable(
    invoice.openingDebt ?? 0,
    incurred,
    invoice.surchargeAmount ?? 0,
    invoice.vatAmount ?? 0,
    invoice.exemptionAmount ?? 0,
  );
}

function hasOlderChainInvoiceInSet(
  invoice: ServiceFeeDebtInvoice,
  allInSet: ServiceFeeDebtInvoice[],
): boolean {
  const billingPeriod = invoice.billingPeriod;
  if (!billingPeriod) return false;
  const key = getInvoiceChainKey(invoice);
  return allInSet.some(
    (other) =>
      other.id !== invoice.id &&
      getInvoiceChainKey(other) === key &&
      !!other.billingPeriod &&
      compareBillingPeriods(other.billingPeriod, billingPeriod) < 0,
  );
}

/** Period charges only — excludes carried opening debt when older periods exist. */
export function getPeriodOnlyOwed(invoice: ServiceFeeDebtInvoice): number {
  return computeClosingDebt(
    0,
    getIncurredBeforeTax(invoice),
    invoice.paidAmount,
    invoice.surchargeAmount ?? 0,
    invoice.vatAmount ?? 0,
    invoice.exemptionAmount ?? 0,
  );
}

export function getInvoiceOwed(invoice: ServiceFeeDebtInvoice): number {
  return Math.max(0, getInvoiceTotalReceivable(invoice) - (invoice.paidAmount || 0));
}

/** Owed for one invoice without double-counting carried debt across periods. */
export function getAllocatableOwed(
  invoice: ServiceFeeDebtInvoice,
  allInSet: ServiceFeeDebtInvoice[],
): number {
  if (hasOlderChainInvoiceInSet(invoice, allInSet)) {
    return getPeriodOnlyOwed(invoice);
  }
  return getInvoiceOwed(invoice);
}

export function sumAllocatableOwed(
  invoices: Array<ServiceFeeDebtInvoice & { status?: string }>,
): number {
  const unpaid = invoices.filter(
    (invoice) => !invoice.status || UNPAID_STATUSES.has(invoice.status),
  );
  return unpaid.reduce((sum, inv) => sum + getAllocatableOwed(inv, unpaid), 0);
}

/** @deprecated Sums every invoice closingDebt and double-counts carried debt across periods. */
export function sumOutstandingReceivable(
  invoices: Array<ServiceFeeDebtInvoice & { status?: string }>,
): number {
  return invoices
    .filter((invoice) => !invoice.status || UNPAID_STATUSES.has(invoice.status))
    .reduce((sum, invoice) => sum + computeInvoiceClosingDebt(invoice), 0);
}

export function sumUnpaidInvoiceAmountsAfterTax(
  invoices: Array<{
    amount: number;
    baseAmount?: number | null;
    vatAmount?: number | null;
    status?: string;
  }>,
): number {
  return invoices
    .filter((invoice) => !invoice.status || UNPAID_STATUSES.has(invoice.status))
    .reduce((sum, invoice) => sum + getInvoiceAmountAfterTax(invoice), 0);
}
